/**
 * Dictionnaire francais.
 *
 * Toute chaine affichee a l'utilisateur passe par ce fichier : l'ajout d'une
 * langue se limitera a creer un fichier de meme forme et a l'enregistrer dans
 * `i18n/index.ts`. Les cles sont hierarchiques et decrites par domaine.
 */
export const fr = {
  app: {
    name: 'FIREGAME',
    tagline: 'Simulation de lutte contre les feux de foret',
    skipToContent: 'Aller au contenu principal',
    loading: 'Chargement en cours...',
  },
  nav: {
    home: 'Accueil',
    scenarios: 'Missions',
    profile: 'Profil',
    settings: 'Parametres',
    help: 'Aide',
    login: 'Connexion',
    register: 'Creer un compte',
    logout: 'Deconnexion',
  },
  home: {
    heroTitle: 'Vous prenez le commandement.',
    heroSubtitle:
      "Un depart de feu vient d'etre signale. Analysez le terrain, engagez les moyens aeriens et terrestres, protegez les habitations et fixez le front avant la tombee de la nuit.",
    startMission: 'Choisir une mission',
    continueMission: 'Reprendre la partie en cours',
    availableSoon: 'Disponible prochainement',
    openDemo: 'Ouvrir la demonstration',
    demoHint:
      'La demonstration montre le moteur de propagation en fonctionnement. Les missions completes arrivent avec les scenarios.',
    pillars: {
      simulation: {
        title: 'Une propagation credible',
        text: "Vent, pente, secheresse, type de vegetation et zones deja brulees determinent l'avancee du front, cellule par cellule.",
      },
      resources: {
        title: 'Des moyens sous contrainte',
        text: "Canadair, Dash, helicopteres, camions-citernes et groupes au sol : delais d'arrivee, autonomie, rotations et fatigue conditionnent chaque decision.",
      },
      stakes: {
        title: 'Des enjeux reels',
        text: 'Habitations, villages, campings, infrastructures sensibles : chaque choix se paie en hectares brules ou en batiments perdus.',
      },
    },
  },
  status: {
    title: 'Etat des services',
    checking: 'Verification des services...',
    online: 'Service operationnel',
    offline: 'Service injoignable',
    degraded: 'Service degrade',
    retry: 'Reessayer',
    version: 'Version',
    engine: 'Moteur',
    environment: 'Environnement',
    uptime: 'En service depuis',
    dependencies: 'Dependances',
    dependencyUp: 'operationnelle',
    dependencyDown: 'indisponible',
    dependencyDisabled: 'non activee',
  },
  notFound: {
    title: 'Page introuvable',
    text: "Cette adresse ne correspond a aucune page. Le poste de commandement vous renvoie a l'accueil.",
    back: "Retour a l'accueil",
  },
  auth: {
    emailLabel: 'Adresse e-mail',
    passwordLabel: 'Mot de passe',
    passwordConfirmLabel: 'Confirmer le mot de passe',
    newPasswordLabel: 'Nouveau mot de passe',
    currentPasswordLabel: 'Mot de passe actuel',
    displayNameLabel: 'Pseudonyme',
    passwordHint: '10 caracteres minimum, avec au moins une lettre et un chiffre.',
    displayNameHint: 'Nom affiche dans les classements et le journal de mission.',
    login: {
      title: 'Connexion',
      subtitle: 'Reprenez le commandement de vos interventions.',
      submit: 'Se connecter',
      forgot: 'Mot de passe oublie ?',
      noAccount: 'Pas encore de compte ?',
      createAccount: 'Creer un compte',
    },
    register: {
      title: 'Creer un compte',
      subtitle: 'Votre progression et vos meilleurs scores seront conserves.',
      submit: 'Creer mon compte',
      haveAccount: 'Deja inscrit ?',
      login: 'Se connecter',
      passwordMismatch: 'Les deux mots de passe ne correspondent pas.',
    },
    forgot: {
      title: 'Mot de passe oublie',
      subtitle:
        'Indiquez votre adresse e-mail : si un compte y est associe, vous recevrez un lien de reinitialisation.',
      submit: 'Envoyer le lien',
      backToLogin: 'Retour a la connexion',
    },
    reset: {
      title: 'Nouveau mot de passe',
      subtitle: 'Choisissez un nouveau mot de passe pour votre compte.',
      submit: 'Enregistrer',
      missingToken: 'Lien de reinitialisation incomplet. Refaites une demande.',
      success: 'Mot de passe modifie. Vous pouvez maintenant vous connecter.',
      goToLogin: 'Aller a la connexion',
    },
    profile: {
      title: 'Mon profil',
      account: 'Compte',
      memberSince: 'Inscrit le',
      lastLogin: 'Derniere connexion',
      never: 'jamais',
      role: 'Role',
      rolePlayer: 'Joueur',
      roleAdmin: 'Administrateur',
      settings: 'Preferences',
      volumeMaster: 'Volume general',
      volumeMusic: 'Musique',
      volumeSfx: 'Effets sonores',
      volumeAmbience: 'Ambiance',
      reducedEffects: 'Reduire les effets visuels',
      reducedEffectsHint: 'Moins de particules et de fumee : utile sur un ordinateur modeste.',
      highContrast: 'Contrastes renforces',
      showHints: 'Afficher les conseils en cours de partie',
      uiScale: "Taille de l'interface",
      saveSettings: 'Enregistrer les preferences',
      settingsSaved: 'Preferences enregistrees.',
      changePassword: 'Changer de mot de passe',
      passwordChanged: 'Mot de passe modifie. Vos autres sessions ont ete fermees.',
    },
    logoutConfirm: 'Se deconnecter ?',
  },
  game: {
    title: 'Poste de commandement',
    demoBanner:
      'Demonstration technique : moteur de propagation et rendu. Les moyens aeriens et terrestres arrivent aux lots suivants.',
    loading: 'Preparation du terrain...',
    speed: {
      label: 'Vitesse',
      pause: 'Pause',
      x1: 'x1',
      x2: 'x2',
      x4: 'x4',
      x8: 'x8',
      resume: 'Reprendre',
    },
    weather: {
      title: 'Meteo',
      wind: 'Vent',
      gusts: 'Rafales',
      temperature: 'Temperature',
      humidity: 'Humidite',
      dryness: 'Secheresse',
      visibility: 'Visibilite',
      localTime: 'Heure locale',
    },
    stats: {
      title: 'Situation',
      elapsed: 'Duree d intervention',
      activeFires: 'Foyers actifs',
      burned: 'Surface brulee',
      preserved: 'Vegetation preservee',
      structures: 'Batiments',
      intact: 'intacts',
      damaged: 'endommages',
      destroyed: 'detruits',
      population: 'Population exposee',
      spotFires: 'Sautes de feu',
      water: 'Eau larguee',
      retardant: 'Retardant largue',
      fireline: 'Lignes d appui',
    },
    journal: {
      title: 'Journal de mission',
      empty: 'Aucun evenement pour le moment.',
    },
    inspector: {
      title: 'Zone selectionnee',
      hint: 'Survolez la carte pour examiner une zone.',
      position: 'Position',
      terrain: 'Terrain',
      state: 'Etat',
      intensity: 'Intensite du feu',
      smoke: 'Fumee',
      water: 'Eau au sol',
      retardant: 'Retardant',
      altitude: 'Altitude',
    },
    fleet: {
      title: 'Moyens aeriens',
      none: 'Aucun moyen aerien engage sur cette intervention.',
      engaged: 'en action',
      load: 'Charge en soute',
      endurance: 'Autonomie restante',
      drops: 'largages',
      selectHint: 'Cliquez sur la carte pour designer la zone de largage.',
      assignedHint: 'Cliquez ailleurs pour deplacer la zone de largage.',
      recall: 'Rappeler',
    },
    ground: {
      title: 'Moyens terrestres',
      none: 'Aucun moyen terrestre engage sur cette intervention.',
      engaged: 'au travail',
      water: 'Eau restante en citerne',
      fatigue: 'Fatigue de l equipage',
      used: 'deverses',
      noTank: 'sans citerne',
      orderLabel: 'Mission a confier',
      clickHint: 'Cliquez sur la carte pour envoyer le moyen.',
      recall: 'Rappeler',
      orders: {
        attack: 'Attaquer',
        defend: 'Defendre',
        fireline: "Ligne d'appui",
      },
    },
    tools: {
      title: 'Ordres directs',
      hint: 'Ces ordres agissent directement sur le terrain. Les moyens qui les executeront arrivent au lot 5.',
      inspect: 'Examiner',
      water: "Largage d'eau",
      retardant: 'Largage de retardant',
      fireline: "Ligne d'appui",
      firelineStart: 'Cliquez le point de depart de la ligne.',
      firelineEnd: "Cliquez le point d'arrivee.",
      ignite: 'Contre-feu',
    },
    controls: {
      title: 'Commandes',
      pan: 'Deplacer la carte',
      zoom: 'Zoomer',
      fit: 'Voir toute la carte',
      pause: 'Pause / reprise',
      speeds: 'Vitesses',
      drag: 'clic glisse',
      wheel: 'molette',
      orbit: 'clic droit ou Maj + glisse : faire pivoter la vue',
      trees: 'arbres affiches',
    },
    quality: {
      label: 'Qualite',
    },
    error: 'Le moteur de simulation a rencontre une erreur :',
  },
  errors: {
    network: 'Le serveur ne repond pas. Verifiez votre connexion, puis reessayez.',
    unexpected: 'Une erreur inattendue est survenue.',
    required: 'Ce champ est obligatoire.',
  },
  units: {
    seconds: 's',
    minutes: 'min',
    hours: 'h',
    hectares: 'ha',
    kmh: 'km/h',
    cubicMeters: 'm3',
    euros: 'EUR',
  },
} as const;

export type Dictionary = typeof fr;
